import { useState, type ReactElement } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'

import { CoaTemplateAccountForm, coaTemplateQuery, useCreateCoaTemplateAccount } from '~/features/coa-templates'
import type { AppError } from '~/lib/result'

/**
 * `/admin/finance/reference-accounts/$templateId/accounts/new` — add one
 * account row to a template. A full page rather than a modal.
 */
export const Route = createFileRoute(
  '/admin/finance/reference-accounts/$templateId/accounts/new',
)({
  component: NewAccountPage,
})

function NewAccountPage(): ReactElement {
  const { templateId } = Route.useParams()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)
  const createAccount = useCreateCoaTemplateAccount()
  const templateQuery = useQuery(coaTemplateQuery(templateId))

  const backTo = () =>
    void navigate({
      to: '/admin/finance/reference-accounts/$templateId',
      params: { templateId },
    })

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm" onClick={backTo}>
            <ArrowLeft size={14} aria-hidden="true" /> Back
          </button>
          <h1 className="ka-page-header-title">Add Account</h1>
        </div>
      </header>

      <CoaTemplateAccountForm
        templateId={templateId}
        codeFormatHint={templateQuery.data?.code_format_hint ?? null}
        account={null}
        isSubmitting={createAccount.isPending}
        serverError={serverError}
        onCancel={backTo}
        onSubmit={(body) => {
          setServerError(null)
          createAccount.mutate(
            { templateId, body: body as Parameters<typeof createAccount.mutate>[0]['body'] },
            {
              onSuccess: backTo,
              onError: (cause) => setServerError(errorToMessage(cause)),
            },
          )
        }}
      />
    </div>
  )
}

function errorToMessage(cause: unknown): string {
  const appError = cause as AppError
  return appError?.message ?? 'Something went wrong. Please try again.'
}
